/**
 * POP-OUT CHAT COMPONENT
 * 
 * A standalone chat window designed to work in a pop-out browser window.
 * Users can browse the marketplace while chatting in this floating window.
 * 
 * Features:
 * - Compact design optimized for small window
 * - Real-time messaging
 * - Pinned items shown in compact view with click to view details
 * - Add items button to add more items to conversation
 * - Can be closed to return to main conversation
 * - Glassmorphic styling
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { subscribeToMessages, sendMessage, removeItemFromConversation, removeCreationFromConversation } from '../messaging/messagingService';
import type { Message, Conversation as ConversationType, ParticipantInfo, AttachedItem, AttachedCreation } from '../messaging/types';
import type { CollectionItem } from '../types';
import type { Creation } from '../types/creationTypes';
import MessageBubble from '../messaging/components/MessageBubble';
import AddItemsModal from '../messaging/components/AddItemsModal';
import MarketplaceItemDetailModal from '../components/marketplace/MarketplaceItemDetailModal';
import MarketplaceCreationDetailModal from '../components/marketplace/MarketplaceCreationDetailModal';
import { Send, Loader2, X, Package, Plus } from 'lucide-react';

// Extended types to match marketplace modals
interface MarketplaceItem extends CollectionItem {
  sellerId: string;
  sellerUsername?: string;
}

interface MarketplaceCreation extends Creation {
  sellerId: string;
  sellerUsername?: string;
}

const PopOutChat: React.FC = () => {
  const { conversationId } = useParams<{ conversationId: string }>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [conversation, setConversation] = useState<ConversationType | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showItems, setShowItems] = useState(false);
  const [otherUser, setOtherUser] = useState<(ParticipantInfo & { id: string }) | null>(null);

  // Modal states
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<MarketplaceItem | null>(null);
  const [selectedCreation, setSelectedCreation] = useState<MarketplaceCreation | null>(null);
  const [loadingItem, setLoadingItem] = useState(false);
  const [loadingCreation, setLoadingCreation] = useState(false);

  // Remove item states
  const [removingItemId, setRemovingItemId] = useState<string | null>(null);
  const [confirmRemoveItem, setConfirmRemoveItem] = useState<{ id: string; name: string; type: 'item' | 'creation' } | null>(null);

  // Set window title
  useEffect(() => {
    if (otherUser) {
      document.title = `Chat with ${otherUser.username ? `@${otherUser.username}` : otherUser.displayName}`;
    } else {
      document.title = 'Chat';
    }
  }, [otherUser]);

  // Get current user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUserId(user?.uid || null);
    });
    return () => unsubscribe();
  }, []);

  // Subscribe to conversation document
  useEffect(() => {
    if (!conversationId || !currentUserId) return;

    const conversationRef = doc(db, 'conversations', conversationId);
    const unsubscribe = onSnapshot(conversationRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data() as ConversationType;
        setConversation({ ...data, id: snapshot.id });

        // Get other user's info
        const otherUserId = data.participants.find(id => id !== currentUserId);
        if (otherUserId && data.participantInfo[otherUserId]) {
          setOtherUser({
            id: otherUserId,
            ...data.participantInfo[otherUserId]
          });
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [conversationId, currentUserId]);

  // Subscribe to messages
  useEffect(() => {
    if (!conversationId) return;

    const unsubscribe = subscribeToMessages(conversationId, (msgs) => {
      setMessages(msgs);
    });

    return () => unsubscribe();
  }, [conversationId]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle viewing item details - fetch full item data and open modal
  const handleViewItemDetails = async (item: AttachedItem) => {
    setLoadingItem(true);
    try {
      const itemRef = doc(db, 'users', item.ownerId, 'items', item.itemId);
      const itemSnap = await getDoc(itemRef);
      
      if (itemSnap.exists()) {
        const itemData = itemSnap.data() as CollectionItem;
        // Get seller info
        const userRef = doc(db, 'users', item.ownerId);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.exists() ? userSnap.data() : {};
        
        setSelectedItem({
          ...itemData,
          id: itemSnap.id,
          sellerId: item.ownerId,
          sellerUsername: userData.username || undefined,
        });
      } else {
        alert('Item not found. It may have been removed.');
      }
    } catch (err) {
      console.error('Error fetching item:', err);
      alert('Failed to load item details.');
    } finally {
      setLoadingItem(false);
    }
  };

  // Handle viewing creation details
  const handleViewCreationDetails = async (creation: AttachedCreation) => {
    setLoadingCreation(true);
    try {
      const creationRef = doc(db, 'users', creation.ownerId, 'creations', creation.creationId);
      const creationSnap = await getDoc(creationRef);
      
      if (creationSnap.exists()) {
        const creationData = creationSnap.data() as Creation;
        // Get seller info
        const userRef = doc(db, 'users', creation.ownerId);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.exists() ? userSnap.data() : {};
        
        setSelectedCreation({
          ...creationData,
          id: creationSnap.id,
          sellerId: creation.ownerId,
          sellerUsername: userData.username || undefined,
        });
      } else {
        alert('Creation not found. It may have been removed.');
      }
    } catch (err) {
      console.error('Error fetching creation:', err);
      alert('Failed to load creation details.');
    } finally {
      setLoadingCreation(false);
    }
  };

  // Handle removing an item from conversation
  const handleRemoveItem = async (itemId: string, itemName: string) => {
    if (!currentUserId || !conversationId) return;
    
    setRemovingItemId(itemId);
    try {
      await removeItemFromConversation(conversationId, currentUserId, itemId, itemName);
      setConfirmRemoveItem(null);
    } catch (err) {
      console.error('Error removing item:', err);
      alert('Failed to remove item. Please try again.');
    } finally {
      setRemovingItemId(null);
    }
  };

  // Handle removing a creation from conversation
  const handleRemoveCreation = async (creationId: string, creationTitle: string) => {
    if (!currentUserId || !conversationId) return;
    
    setRemovingItemId(creationId);
    try {
      await removeCreationFromConversation(conversationId, currentUserId, creationId, creationTitle);
      setConfirmRemoveItem(null);
    } catch (err) {
      console.error('Error removing creation:', err);
      alert('Failed to remove creation. Please try again.');
    } finally {
      setRemovingItemId(null);
    }
  };

  // Send message handler
  const handleSend = async () => {
    if (!newMessage.trim() || !currentUserId || !conversationId || sending) return;

    setSending(true);
    const messageText = newMessage.trim();
    setNewMessage('');

    try {
      await sendMessage(conversationId, {
        senderId: currentUserId,
        type: 'text',
        text: messageText,
        readBy: [currentUserId],
      });
    } catch (err) {
      console.error('Error sending message:', err);
      setNewMessage(messageText);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  // Handle enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Close window
  const handleClose = () => {
    window.close();
  };

  // Total pinned count
  const pinnedItemCount = (conversation?.pinnedItems?.length || 0) + (conversation?.pinnedCreations?.length || 0);

  // Get existing item/creation IDs for AddItemsModal
  const existingItemIds = conversation?.pinnedItems?.map(item => item.itemId) || [];
  const existingCreationIds = conversation?.pinnedCreations?.map(c => c.creationId) || [];

  // Loading state
  if (loading) {
    return (
      <div className="h-screen bg-gradient-to-b from-fuchsia-500 via-yellow-500 via-green-500 to-blue-500 flex items-center justify-center">
        <div className="glass-card p-6 text-center">
          <Loader2 className="w-8 h-8 text-glass animate-spin mx-auto mb-2" />
          <p className="text-glass-secondary text-sm font-body">Loading...</p>
        </div>
      </div>
    );
  }

  // Not logged in or conversation not found
  if (!conversation || !currentUserId) {
    return (
      <div className="h-screen bg-gradient-to-b from-fuchsia-500 via-yellow-500 via-green-500 to-blue-500 flex items-center justify-center p-4">
        <div className="glass-card p-6 text-center max-w-sm">
          <p className="text-glass-secondary mb-4 font-body">
            Unable to load conversation. Please make sure you're logged in.
          </p>
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-slate-700 text-white rounded-lg font-bold hover:bg-slate-800 transition-default font-body"
          >
            Close Window
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-b from-fuchsia-500 via-yellow-500 via-green-500 to-blue-500 flex flex-col">
      {/* Loading overlay when fetching item details */}
      {(loadingItem || loadingCreation) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]">
          <div className="glass-card p-4 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-glass animate-spin" />
            <span className="font-body text-glass-secondary">Loading details...</span>
          </div>
        </div>
      )}

      {/* Compact Header */}
      <div className="bg-glass border-b border-slate-600 text-white p-3 flex items-center gap-3 shadow-md flex-shrink-0">
        {/* Avatar */}
        {otherUser?.avatarUrl ? (
          <img 
            src={otherUser.avatarUrl} 
            alt={otherUser.displayName}
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-bold text-sm">
            {otherUser?.displayName?.charAt(0).toUpperCase() || '?'}
          </div>
        )}
        
        {/* Name */}
        <div className="flex-1 min-w-0">
          <h2 className="font-bold text-sm truncate font-body">
            {otherUser?.username ? `@${otherUser.username}` : otherUser?.displayName}
          </h2>
        </div>

        {/* Add Items button */}
        <button
          onClick={() => setShowAddItemsModal(true)}
          className="p-1.5 bg-white/10 hover:bg-white/20 rounded-full transition-default"
          title="Add items to discussion"
        >
          <Plus className="icon-sm" />
        </button>

        {/* Items toggle */}
        {pinnedItemCount > 0 && (
          <button
            onClick={() => setShowItems(!showItems)}
            className={`relative p-1.5 rounded-full transition-default ${showItems ? 'bg-white/30' : 'bg-white/10 hover:bg-white/20'}`}
            title={`${pinnedItemCount} items in discussion`}
          >
            <Package className="icon-sm" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-fuchsia-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
              {pinnedItemCount}
            </span>
          </button>
        )}
        
        {/* Close button */}
        <button
          onClick={handleClose}
          className="p-1.5 bg-white/10 hover:bg-white/20 rounded-full transition-default"
          title="Close chat window"
        >
          <X className="icon-sm" />
        </button>
      </div>

      {/* Compact Items Panel (collapsible) */}
      {showItems && pinnedItemCount > 0 && (
        <div className="backdrop-blur-sm bg-slate-800/40 border-b border-slate-600 p-2 max-h-40 overflow-y-auto flex-shrink-0">
          {/* Confirmation overlay */}
          {confirmRemoveItem && (
            <div className="mb-2 p-2 bg-slate-900/80 rounded-lg border border-slate-500">
              <p className="text-xs text-glass-secondary text-center mb-2 font-body">
                Remove "{confirmRemoveItem.name}"?
              </p>
              <div className="flex justify-center gap-2">
                <button
                  onClick={() => setConfirmRemoveItem(null)}
                  className="px-3 py-1 text-xs text-glass-secondary hover:bg-slate-700 rounded transition-default font-body"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (confirmRemoveItem.type === 'item') {
                      handleRemoveItem(confirmRemoveItem.id, confirmRemoveItem.name);
                    } else {
                      handleRemoveCreation(confirmRemoveItem.id, confirmRemoveItem.name);
                    }
                  }}
                  disabled={removingItemId === confirmRemoveItem.id}
                  className="px-3 py-1 text-xs bg-red-500 text-white hover:bg-red-600 rounded transition-default font-body disabled:opacity-50"
                >
                  {removingItemId === confirmRemoveItem.id ? 'Removing...' : 'Remove'}
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-1">
            {conversation.pinnedItems?.map((item, index) => (
              <div 
                key={`item-${item.itemId}-${index}`}
                className="group relative inline-flex items-center gap-1.5 bg-slate-800/60 hover:bg-slate-700/80 rounded px-2 py-1 text-xs border border-slate-600 transition-colors"
              >
                {/* Remove button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmRemoveItem({ id: item.itemId, name: item.name, type: 'item' });
                  }}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity hidden group-hover:flex z-10"
                  title="Remove from conversation"
                >
                  ×
                </button>

                <button
                  onClick={() => handleViewItemDetails(item)}
                  className="flex items-center gap-1.5 text-left"
                  title={`View ${item.name} details`}
                >
                  {item.photoUrl ? (
                    <img src={item.photoUrl} alt="" className="w-6 h-6 rounded object-cover" />
                  ) : (
                    <div className="w-6 h-6 rainbow-gradient rounded flex items-center justify-center text-[10px]">
                      💅
                    </div>
                  )}
                  <span className="font-bold text-glass-secondary truncate max-w-[70px] font-body">
                    {item.name}
                  </span>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    item.status === 'sold' ? 'bg-red-500' : 
                    item.status === 'pending' ? 'bg-yellow-500' : 'bg-green-500'
                  }`} />
                </button>
              </div>
            ))}
            {conversation.pinnedCreations?.map((creation, index) => (
              <div 
                key={`creation-${creation.creationId}-${index}`}
                className="group relative inline-flex items-center gap-1.5 bg-purple-900/40 hover:bg-purple-800/60 rounded px-2 py-1 text-xs border border-purple-500/50 transition-colors"
              >
                {/* Remove button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmRemoveItem({ id: creation.creationId, name: creation.title, type: 'creation' });
                  }}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity hidden group-hover:flex z-10"
                  title="Remove from conversation"
                >
                  ×
                </button>

                <button
                  onClick={() => handleViewCreationDetails(creation)}
                  className="flex items-center gap-1.5 text-left"
                  title={`View ${creation.title} details`}
                >
                  {creation.photoUrl ? (
                    <img src={creation.photoUrl} alt="" className="w-6 h-6 rounded object-cover" />
                  ) : (
                    <div className="w-6 h-6 bg-gradient-to-br from-purple-400 to-pink-600 rounded flex items-center justify-center text-[10px]">
                      ✨
                    </div>
                  )}
                  <span className="font-bold text-purple-200 truncate max-w-[70px] font-body">
                    {creation.title}
                  </span>
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    creation.status === 'sold' ? 'bg-red-500' : 
                    creation.status === 'pending' ? 'bg-yellow-500' : 'bg-green-500'
                  }`} />
                </button>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-glass-muted mt-1 text-center">Click to view • Hover to remove</p>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2 backdrop-blur-sm bg-slate-800/20 min-h-0">
        {messages.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-glass-muted text-sm font-body">
              No messages yet. Say hi! 👋
            </p>
          </div>
        ) : (
          messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              isOwnMessage={message.senderId === currentUserId}
              senderName={
                message.senderId === currentUserId 
                  ? 'You' 
                  : otherUser?.displayName || 'Unknown'
              }
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="bg-glass border-t border-slate-600 p-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="flex-1 px-3 py-2 border border-slate-600 rounded-full text-sm focus:ring-2 focus:ring-slate-500 focus:border-slate-600 bg-white/80 font-body"
            disabled={sending}
            autoFocus
          />
          <button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            className={`p-2 rounded-full transition-default ${
              newMessage.trim() && !sending
                ? 'btn-primary'
                : 'bg-gray-200 text-glass-muted cursor-not-allowed'
            }`}
          >
            {sending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="icon-sm" />
            )}
          </button>
        </div>
      </div>

      {/* Open full conversation link */}
      <div className="bg-glass-light px-3 py-1.5 text-center border-t border-slate-600 flex-shrink-0">
        <a 
          href={`/messages/${conversationId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-glass hover:text-glass-secondary hover:underline font-body"
        >
          Open full conversation →
        </a>
      </div>

      {/* Add Items Modal */}
      {otherUser && (
        <AddItemsModal
          isOpen={showAddItemsModal}
          onClose={() => setShowAddItemsModal(false)}
          conversationId={conversationId!}
          currentUserId={currentUserId}
          otherUserId={otherUser.id}
          existingItemIds={existingItemIds}
          existingCreationIds={existingCreationIds}
          onItemsAdded={() => {
            // Modal will close itself, items update via realtime listener
          }}
        />
      )}

      {/* Item Detail Modal */}
      {selectedItem && (
        <MarketplaceItemDetailModal
          item={selectedItem}
          items={[selectedItem]}
          currentIndex={0}
          currentUserId={currentUserId}
          onClose={() => setSelectedItem(null)}
          onNavigate={() => {}}
          onCopySuccess={() => {}}
        />
      )}

      {/* Creation Detail Modal */}
      {selectedCreation && (
        <MarketplaceCreationDetailModal
          creation={selectedCreation}
          creations={[selectedCreation]}
          currentIndex={0}
          currentUserId={currentUserId}
          onClose={() => setSelectedCreation(null)}
          onNavigate={() => {}}
        />
      )}
    </div>
  );
};

export default PopOutChat;
